import React, { useState, useEffect } from 'react';
import { customFields } from '../../utils/api/customFields';

const CustomFieldsFormSection = ({ coinId, initialValues = {}, onChange }) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Initialize values as simple strings
  const parseValue = (value) => {
    if (!value) return '';
    if (typeof value === 'object' && 'value' in value) {
      return value.value || '';
    }
    try {
      // If it's a JSON string, parse it
      const parsed = JSON.parse(value);
      return parsed.value || '';
    } catch (e) {
      // If it's not JSON, return as is
      return value;
    }
  };

  const [values, setValues] = useState(() => {
    const structured = {};
    Object.entries(initialValues || {}).forEach(([key, value]) => {
      structured[key] = parseValue(value);
    });
    return structured;
  });

  // Load custom fields and their values
  useEffect(() => {
    const loadData = async () => {
      try {
        const [fieldsResponse, valuesResponse] = await Promise.all([
          customFields.getAll(),
          coinId ? customFields.getValues(coinId) : Promise.resolve({})
        ]);

        if (mounted) {
          setFields(fieldsResponse);
          // Preserve existing values while adding/updating with new ones
          setValues(prev => {
            const structured = { ...prev };
            Object.entries(valuesResponse || {}).forEach(([key, value]) => {
              structured[key] = parseValue(value);
            });
            return structured;
          });
        }
        setError(null);
      } catch (err) {
        console.error('Error loading custom fields:', err);
        setError('Failed to load custom fields');
      }
    };

    let mounted = true;
    loadData().finally(() => {
      if (!mounted) return;
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [coinId]);

  // Update parent component when values change
  useEffect(() => {
    // Always structure values in the format expected by the API
    const structuredValues = Object.entries(values).reduce((acc, [key, value]) => {
      // Always wrap in an object with value property
      acc[key] = { value: parseValue(value) };
      return acc;
    }, {});
    onChange?.(structuredValues);
  }, [values]); // Remove onChange from dependencies since it's stable

  const handleChange = (fieldId, value) => {
    setValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  if (loading) {
    return null; // Don't show anything while loading
  }

  if (error || fields.length === 0) {
    return null; // Don't show section if there's an error or no custom fields
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {fields.map(field => (
        <div key={field.id}>
          <label htmlFor={`custom_field_${field.id}`} className="block text-sm font-medium text-gray-700 mb-2">
            {field.name}
          </label>
          <input
            type="text"
            id={`custom_field_${field.id}`}
            value={parseValue(values[field.id]) || ''}
            onChange={(e) => handleChange(field.id, e.target.value)}
            maxLength={field.max_length}
            className="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
          />
        </div>
      ))}
    </div>
  );
};

export default CustomFieldsFormSection;
